"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.groupTaskList = void 0;
const luxon_1 = require("luxon");
const types_1 = require("../../../types");
const components_1 = require("../../../components");
const useAnomalySearch_1 = require("../../../hooks/useAnomalySearch");
const utils_1 = require("../../../utils");
const groupTaskList = (punchesTimeSegments = [], punchCorrStatusList = [], punchCorrections = [], payPeriodInfo = undefined, anomalySearchList = [], isAnomalySearchEnabled) => {
    const punchCorrectionStatusGroup = groupPunchCorrectionStatusList(punchCorrStatusList);
    const duplicatedPunchGroup = isAnomalySearchEnabled
        ? formatDuplicatedPunches(anomalySearchList, payPeriodInfo)
        : groupDuplicatedPunches(punchesTimeSegments, payPeriodInfo);
    const missingPunchGroup = isAnomalySearchEnabled
        ? formatMissingPunches(anomalySearchList)
        : groupMissingPunches(punchesTimeSegments, payPeriodInfo);
    const systemAddedPunchGroup = groupSystemAddedPunched(punchCorrections, punchesTimeSegments, payPeriodInfo);
    const requireAttentionGroup = [
        ...duplicatedPunchGroup,
        ...missingPunchGroup,
        ...systemAddedPunchGroup
    ].sort((a, b) => dateTimeComparator(a.dateTimeString, b.dateTimeString));
    return [
        {
            key: types_1.TaskListTypes.RequireAttention,
            count: punchCorrectionStatusGroup.failed.length +
                duplicatedPunchGroup.length +
                missingPunchGroup.length +
                systemAddedPunchGroup.length,
            titleKey: 'time_and_attendance_TaskList_RequireAttention',
            missingPunches: missingPunchGroup,
            duplicatedPunches: duplicatedPunchGroup,
            systemAddedPunches: systemAddedPunchGroup,
            requireAttentionItems: requireAttentionGroup,
            punchCorrectionStatus: punchCorrectionStatusGroup.failed
        },
        {
            key: types_1.TaskListTypes.PendingRequest,
            count: punchCorrectionStatusGroup.pending.length,
            titleKey: 'time_and_attendance_TaskList_PendingRequests',
            punchCorrectionStatus: punchCorrectionStatusGroup.pending
        },
        {
            key: types_1.TaskListTypes.CompletedRequest,
            count: punchCorrectionStatusGroup.success.length,
            titleKey: 'time_and_attendance_TaskList_CompletedRequests',
            punchCorrectionStatus: punchCorrectionStatusGroup.success
        }
    ];
};
exports.groupTaskList = groupTaskList;
const filterPunchCorrectionStatusList = (list) => {
    const filteredMap = list.reduce((filteredList, item) => {
        if (!filteredList.has(item.requestId)) {
            filteredList.set(item.requestId, item);
            return filteredList;
        }
        const savedRequest = filteredList.get(item.requestId);
        if (luxon_1.DateTime.fromISO(savedRequest.updateTime) <
            luxon_1.DateTime.fromISO(item.updateTime)) {
            filteredList.set(item.requestId, item);
        }
        return filteredList;
    }, new Map());
    return Array.from(filteredMap.values());
};
const dateTimeComparator = (aDateString, bDateString) => +luxon_1.DateTime.fromISO(bDateString) - +luxon_1.DateTime.fromISO(aDateString);
const groupPunchCorrectionStatusList = (list) => {
    const dedupList = filterPunchCorrectionStatusList(list);
    const sortedList = dedupList.sort((a, b) => {
        const requestTypeWeight = {
            [components_1.PunchCorrType.MISSED_PUNCH]: 1,
            [components_1.PunchCorrType.EDIT_PUNCH]: 2,
            [components_1.PunchCorrType.DELETE_PUNCH]: 3,
            [components_1.PunchCorrType.A_TO_Z_CLOCK_PUNCH]: 4
        };
        if (a.requestType === b.requestType) {
            return dateTimeComparator(a.punchDateTime, b.punchDateTime);
        }
        return requestTypeWeight[a.requestType] - requestTypeWeight[b.requestType];
    });
    const successList = sortedList.filter((item) => [
        components_1.PunchCorrStatus.COMPLETED_SUCCESSFULLY,
        components_1.PunchCorrStatus.SUBMITTED_SUCCESSFULLY
    ].includes(item.status));
    const failedList = sortedList.filter((item) => {
        if (item.requestType === components_1.PunchCorrType.DELETE_PUNCH) {
            return item.status === components_1.PunchCorrStatus.FAILED;
        }
        if (item.requestType === components_1.PunchCorrType.A_TO_Z_CLOCK_PUNCH) {
            return item.status === components_1.PunchCorrStatus.FAILED;
        }
        if (item.requestType === components_1.PunchCorrType.MISSED_PUNCH ||
            item.requestType === components_1.PunchCorrType.EDIT_PUNCH) {
            return (item.status === components_1.PunchCorrStatus.FAILED &&
                item.reasonCode !== components_1.PunchCorrReason.DUPLICATE);
        }
        return false;
    });
    const pendingList = sortedList.filter((item) => {
        const pendingStatus = [
            components_1.PunchCorrStatus.IN_PROGRESS,
            components_1.PunchCorrStatus.PENDING,
            components_1.PunchCorrStatus.FAILED_PENDING_RETRY
        ];
        if (item.status === components_1.PunchCorrStatus.FAILED &&
            (item.requestType === components_1.PunchCorrType.MISSED_PUNCH ||
                item.requestType === components_1.PunchCorrType.EDIT_PUNCH)) {
            return item.reasonCode === components_1.PunchCorrReason.DUPLICATE;
        }
        return pendingStatus.includes(item.status);
    });
    return {
        success: successList,
        pending: pendingList,
        failed: failedList
    };
};
const formatDuplicatedPunches = (anomalySearchList, payPeriodInfo) => {
    const currPayPeriodStart = payPeriodInfo === null || payPeriodInfo === void 0 ? void 0 : payPeriodInfo.current.start_time;
    const currPayPeriodEnd = payPeriodInfo === null || payPeriodInfo === void 0 ? void 0 : payPeriodInfo.current.end_time;
    const hasPayPeriod = currPayPeriodStart && currPayPeriodEnd;
    const result = anomalySearchList.reduce((accumulate, anomaly) => {
        if (anomaly.deviationName === useAnomalySearch_1.AnomalyTypes.DUPLICATE_PUNCH &&
            hasPayPeriod &&
            (0, utils_1.isWithinDateTimeRange)(anomaly.effectiveDateTime, currPayPeriodStart, currPayPeriodEnd)) {
            accumulate.push({
                dateTimeString: anomaly.effectiveDateTime,
                anomalyType: types_1.TaskListAnomalyTypes.DuplicatePunch
            });
        }
        return accumulate;
    }, []);
    return result.sort((a, b) => dateTimeComparator(a.dateTimeString, b.dateTimeString));
};
const groupDuplicatedPunches = (punchesTimeSegments, payPeriodInfo) => {
    const currPayPeriodStart = payPeriodInfo === null || payPeriodInfo === void 0 ? void 0 : payPeriodInfo.current.start_time;
    const currPayPeriodEnd = payPeriodInfo === null || payPeriodInfo === void 0 ? void 0 : payPeriodInfo.current.end_time;
    const hasPayPeriod = currPayPeriodStart && currPayPeriodEnd;
    const result = punchesTimeSegments.reduce((accumulate, punchTimeSegment) => {
        var _a, _b;
        if (punchTimeSegment.startDateTime &&
            hasPayPeriod &&
            (0, utils_1.isWithinDateTimeRange)(punchTimeSegment.startDateTime, currPayPeriodStart, currPayPeriodEnd) &&
            ((_a = punchTimeSegment.eligibility) === null || _a === void 0 ? void 0 : _a.startTimeDeletable)) {
            accumulate.push({
                dateTimeString: punchTimeSegment.startDateTime,
                punchType: components_1.PunchType.in,
                anomalyType: types_1.TaskListAnomalyTypes.DuplicatePunch
            });
        }
        if (punchTimeSegment.endDateTime &&
            hasPayPeriod &&
            (0, utils_1.isWithinDateTimeRange)(punchTimeSegment.endDateTime, currPayPeriodStart, currPayPeriodEnd) &&
            ((_b = punchTimeSegment.eligibility) === null || _b === void 0 ? void 0 : _b.endTimeDeletable)) {
            accumulate.push({
                dateTimeString: punchTimeSegment.endDateTime,
                punchType: components_1.PunchType.out,
                anomalyType: types_1.TaskListAnomalyTypes.DuplicatePunch
            });
        }
        return accumulate;
    }, []);
    return result.sort((a, b) => dateTimeComparator(a.dateTimeString, b.dateTimeString));
};
const formatMissingPunches = (anomalySearchList) => {
    const result = anomalySearchList.reduce((accumulate, anomaly) => {
        if (anomaly.deviationName === useAnomalySearch_1.AnomalyTypes.MISSED_PUNCH) {
            accumulate.push({
                dateTimeString: anomaly.referenceDate,
                anomalyType: types_1.TaskListAnomalyTypes.MissingPunch
            });
        }
        return accumulate;
    }, []);
    return result.sort((a, b) => dateTimeComparator(a.dateTimeString, b.dateTimeString));
};
const groupMissingPunches = (punchesTimeSegments, payPeriodInfo) => {
    const prevPayPeriodStart = (payPeriodInfo === null || payPeriodInfo === void 0 ? void 0 : payPeriodInfo.previous.start_time) ||
        luxon_1.DateTime.now().startOf('day').minus({ days: 14 });
    const currPayPeriodEnd = (payPeriodInfo === null || payPeriodInfo === void 0 ? void 0 : payPeriodInfo.current.end_time) || luxon_1.DateTime.now().endOf('day');
    const missingPunchDateSet = new Set();
    const result = punchesTimeSegments.reduce((accumulate, punchTimeSegment) => {
        const punchDateTime = punchTimeSegment.startDateTime || punchTimeSegment.endDateTime;
        const punchType = !punchTimeSegment.startDateTime
            ? components_1.PunchType.in
            : components_1.PunchType.out;
        const dateStr = luxon_1.DateTime.fromISO(punchDateTime).toLocaleString(luxon_1.DateTime.DATE_SHORT);
        if ((0, utils_1.isWithinDateTimeRange)(punchDateTime, prevPayPeriodStart, currPayPeriodEnd) &&
            (!punchTimeSegment.startDateTime || !punchTimeSegment.endDateTime) &&
            !missingPunchDateSet.has(`${punchType}${dateStr}`)) {
            accumulate.push({
                dateTimeString: punchDateTime,
                punchType,
                anomalyType: types_1.TaskListAnomalyTypes.MissingPunch
            });
            missingPunchDateSet.add(`${punchType}${dateStr}`);
        }
        return accumulate;
    }, []);
    return result.sort((a, b) => dateTimeComparator(a.dateTimeString, b.dateTimeString));
};
const groupSystemAddedPunched = (punchCorrections, punchesTimeSegments, payPeriodInfo) => {
    const punchInSet = new Set();
    const punchOutSet = new Set();
    punchesTimeSegments.forEach((punchesTimeSegment) => {
        if (punchesTimeSegment.startDateTime) {
            punchInSet.add(luxon_1.DateTime.fromISO(punchesTimeSegment.startDateTime)
                .set({
                second: 0,
                millisecond: 0
            })
                .toMillis());
        }
        if (punchesTimeSegment.endDateTime) {
            punchOutSet.add(luxon_1.DateTime.fromISO(punchesTimeSegment.endDateTime)
                .set({
                second: 0,
                millisecond: 0
            })
                .toMillis());
        }
    });
    const currPayPeriodStart = payPeriodInfo === null || payPeriodInfo === void 0 ? void 0 : payPeriodInfo.current.start_time;
    const currPayPeriodEnd = payPeriodInfo === null || payPeriodInfo === void 0 ? void 0 : payPeriodInfo.current.end_time;
    const result = punchCorrections.reduce((accumulate, punchCorrection) => {
        if ((0, utils_1.isWithinDateTimeRange)(punchCorrection.correctionDateTime, currPayPeriodStart, currPayPeriodEnd)) {
            const correctionDateTimeMillis = luxon_1.DateTime.fromISO(punchCorrection.correctionDateTime)
                .set({ second: 0, millisecond: 0 })
                .toMillis();
            let punchType = null;
            if (punchInSet.has(correctionDateTimeMillis)) {
                punchType = components_1.PunchType.in;
            }
            if (punchOutSet.has(correctionDateTimeMillis)) {
                punchType = components_1.PunchType.out;
            }
            accumulate.push({
                dateTimeString: punchCorrection.correctionDateTime,
                punchType: punchType !== null && punchType !== void 0 ? punchType : components_1.PunchType.in,
                anomalyType: types_1.TaskListAnomalyTypes.SystemAddedPunch
            });
        }
        return accumulate;
    }, []);
    return result.sort((a, b) => dateTimeComparator(a.dateTimeString, b.dateTimeString));
};
